import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StatusPill } from '@/components/px/StatusPill'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  decideGate,
  loadPayload,
  loadSnapshots,
  selectConfigPayload,
  selectDecidingGate,
  selectGatesError,
  selectSnapshots
} from '../slices/phaseGatesSlice'
import { GATE_DECISIONS, type GateDecision, type GateDecisionInput, type PhaseGateView } from '../types'

/**
 * The gate decision form (PX-W6.3, AC-2/AC-3). Controlled state + explicit
 * validation (the repo's landed form pattern — see the completion note for
 * the RHF+Zod plan deviation), posting `GateDecisionRequest` verbatim:
 *
 * - RETURN requires `comments`;
 * - an override requires `overrideReason`;
 * - approving a gate whose latest decision was a RETURN requires `resolvesCr`
 *   (the change request that return raised);
 * - flow.04 APPROVE additionally sends `payloadDigest` (of the LIVE payload
 *   the approver actually saw — the server refuses a stale digest) and
 *   `lockedConfigVersion` (the next snapshot number).
 *
 * A keyed 400 renders the server's message inline (`role="alert"`) — and the
 * dialog never claims an audit trail for a refusal (INV-7: only
 * ACTION_STEP_BLOCKED writes one, server-side).
 */

export interface GateDecisionDialogProps {
  orgId: string
  projectId: string
  gate: PhaseGateView
  onClose: () => void
}

interface FormErrors {
  comments?: string
  overrideReason?: string
  resolvesCr?: string
}

export function GateDecisionDialog({ orgId, projectId, gate, onClose }: GateDecisionDialogProps) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()

  const [decision, setDecision] = useState<GateDecision>(GATE_DECISIONS[0])
  const [comments, setComments] = useState('')
  const [override, setOverride] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  const [resolvesCr, setResolvesCr] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})

  const deciding = useAppSelector(selectDecidingGate)
  const serverError = useAppSelector(selectGatesError)
  const payload = useAppSelector(selectConfigPayload)
  const snapshots = useAppSelector(selectSnapshots)

  const isFlow04 = gate.ref === 'flow.04'
  // Approving after a return must reference the CR that return raised.
  const needsResolvesCr = gate.decision === 'RETURN' || gate.changeRequestId !== undefined

  // The live payload (digest) + the locked versions, for the flow.04 approve.
  useEffect(() => {
    if (!isFlow04) return
    void dispatch(loadPayload({ orgId, projectId, gateRef: gate.ref }))
    void dispatch(loadSnapshots({ orgId, projectId }))
  }, [isFlow04, orgId, projectId, gate.ref, dispatch])

  const nextConfigVersion = useMemo(() => {
    const max = snapshots.reduce((acc, row) => Math.max(acc, row.version), 0)
    return String(max + 1)
  }, [snapshots])

  const onSubmitClicked = (): void => {
    const nextErrors: FormErrors = {}
    if (decision === 'RETURN' && comments.trim() === '') {
      nextErrors.comments = t('gates.dialog.commentsRequired')
    }
    if (override && overrideReason.trim() === '') {
      nextErrors.overrideReason = t('gates.dialog.overrideReasonRequired')
    }
    if (needsResolvesCr && decision === GATE_DECISIONS[0] && resolvesCr.trim() === '') {
      nextErrors.resolvesCr = t('gates.dialog.resolvesCrRequired')
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    // The wire body carries ONLY the fields the approver actually set
    // (conditional spreads keep absent keys OFF the object; absent ≠ null on
    // the backend's request DTO). Built AS a GateDecisionInput — no cast, so
    // the `decision` union is enforced at exactly this seam: a misspelled
    // literal is a compile error here, not a server refusal.
    const commentsValue = comments.trim()
    const overrideReasonValue = overrideReason.trim()
    const crValue = resolvesCr.trim()
    const input: GateDecisionInput = {
      decision,
      ...(commentsValue !== '' ? { comments: commentsValue } : {}),
      ...(override
        ? {
            override: true,
            ...(overrideReasonValue !== '' ? { overrideReason: overrideReasonValue } : {})
          }
        : {}),
      ...(needsResolvesCr && crValue !== '' ? { resolvesCr: crValue } : {}),
      ...(isFlow04 &&
      decision === GATE_DECISIONS[0] &&
      payload?.payloadDigest !== undefined
        ? { lockedConfigVersion: nextConfigVersion, payloadDigest: payload.payloadDigest }
        : {})
    }

    void dispatch(
      decideGate({
        orgId,
        projectId,
        gateRef: gate.ref,
        input
      })
    )
      .unwrap()
      .then(() => onClose())
      .catch(() => {
        // The keyed 400 landed in the slice — it renders inline below.
      })
  }

  const busy = deciding === gate.ref
  const flow04DigestMissing = isFlow04 && decision === GATE_DECISIONS[0] && payload?.payloadDigest === undefined
  const inlineServerError =
    serverError !== null && serverError.gateRef === gate.ref ? serverError : null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('gates.dialog.title', { gate: gate.ref })}
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 p-4"
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-lg" data-testid="gate-decision-dialog">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-serif text-lg font-semibold text-foreground">
              {t('gates.dialog.title', { gate: gate.ref })}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{gate.title}</p>
          </div>
          <StatusPill tone={gate.open ? 'proposed' : 'decided'}>{gate.step}</StatusPill>
        </div>

        <fieldset className="mt-4">
          <legend className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {t('gates.dialog.decision')}
          </legend>
          <div className="mt-1.5 flex gap-2">
            {GATE_DECISIONS.map((value) => (
              <label
                key={value}
                className={`flex flex-1 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                  decision === value ? 'border-ring bg-muted font-medium' : 'border-input'
                }`}
              >
                <input
                  type="radio"
                  name="gate-decision"
                  value={value}
                  checked={decision === value}
                  onChange={() => setDecision(value)}
                />
                {value === GATE_DECISIONS[0] ? t('gates.dialog.approve') : t('gates.dialog.return')}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-3">
          <label htmlFor="gate-comments" className="block text-sm font-medium text-foreground">
            {t('gates.dialog.comments')}
          </label>
          <textarea
            id="gate-comments"
            value={comments}
            onChange={(event) => setComments(event.target.value)}
            placeholder={t('gates.dialog.commentsPlaceholder')}
            rows={3}
            aria-invalid={errors.comments !== undefined}
            aria-describedby={errors.comments !== undefined ? 'gate-comments-error' : undefined}
            className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground"
          />
          {errors.comments !== undefined && (
            <p id="gate-comments-error" role="alert" className="mt-1 text-xs text-rose-ink">
              {errors.comments}
            </p>
          )}
        </div>

        <div className="mt-3">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={override}
              onChange={(event) => setOverride(event.target.checked)}
            />
            {t('gates.dialog.override')}
          </label>
          {override && (
            <div className="mt-2">
              <input
                type="text"
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.target.value)}
                placeholder={t('gates.dialog.overrideReason')}
                aria-label={t('gates.dialog.overrideReason')}
                aria-invalid={errors.overrideReason !== undefined}
                className="w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm text-foreground"
              />
              {errors.overrideReason !== undefined && (
                <p role="alert" className="mt-1 text-xs text-rose-ink">
                  {errors.overrideReason}
                </p>
              )}
            </div>
          )}
        </div>

        {needsResolvesCr && (
          <div className="mt-3">
            <label htmlFor="gate-resolves-cr" className="block text-sm font-medium text-foreground">
              {t('gates.dialog.resolvesCr')}
            </label>
            <input
              id="gate-resolves-cr"
              type="text"
              value={resolvesCr}
              onChange={(event) => setResolvesCr(event.target.value)}
              placeholder={gate.changeRequestId ?? ''}
              aria-invalid={errors.resolvesCr !== undefined}
              className="mt-1 w-full rounded-md border border-input bg-card px-3 py-1.5 font-mono text-sm text-foreground"
            />
            {errors.resolvesCr !== undefined ? (
              <p role="alert" className="mt-1 text-xs text-rose-ink">
                {errors.resolvesCr}
              </p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('gates.dialog.resolvesCrHint', { cr: gate.changeRequestId ?? '—' })}
              </p>
            )}
          </div>
        )}

        {isFlow04 && decision === GATE_DECISIONS[0] && (
          <dl className="mt-3 grid grid-cols-2 gap-3 rounded-md border border-border bg-muted px-3 py-2 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">{t('gates.dialog.lockedConfigVersion')}</dt>
              <dd className="v-figure font-semibold tabular-nums text-foreground">{nextConfigVersion}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t('gates.dialog.payloadDigest')}</dt>
              <dd className="truncate font-mono text-xs text-foreground" title={payload?.payloadDigest}>
                {payload?.payloadDigest ?? t('gates.dialog.payloadDigestLoading')}
              </dd>
            </div>
          </dl>
        )}

        {inlineServerError !== null && (
          <p role="alert" data-testid="gate-server-error" className="mt-3 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
            {t('gates.dialog.serverError')}: {inlineServerError.message}
            {inlineServerError.code !== undefined && (
              <span className="ml-1 font-mono text-xs text-muted-foreground">{inlineServerError.code}</span>
            )}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            {t('gates.dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={onSubmitClicked}
            disabled={busy || flow04DigestMissing}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            data-testid="gate-decision-submit"
          >
            {busy ? t('gates.dialog.submitting') : t('gates.dialog.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
